<script setup lang="ts">
import { computed, defineModel, inject, onMounted, ref } from 'vue'
import { useOpportunityStore } from '@/stores/useOpportunityStore'
import { storeToRefs } from 'pinia'
import DraggableList from '@/components/common/ui/VueDraggable.vue'
import { useToast } from 'vue-toast-notification'
import type { Opportunity } from '@/api-swagger'
import { printCurrency } from '@/utils/numbers'
import type { Context } from '@/utils/types'
import Multiselect from 'vue-multiselect'
import Cookies from 'js-cookie'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import { ArrowPathIcon, EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline'

const COOKIE_NAME = 'selected_columns_kanban'
const COOKIE_EXPIRY = 365 * 10
const viewSelectColumns = ref(false)

const props = defineProps({
  data: {
    type: Array<Opportunity>,
    required: false,
  },
})

const $toast = useToast()
const opportunityStore = useOpportunityStore()
const { stages } = storeToRefs(opportunityStore)

const modelValue = defineModel<object | number | number[] | null>()
const availableColumns = computed(() => {
  return (
    stages.value?.map((stage) => ({
      id: stage.id,
      text: stage.name,
      label: stage.name,
    })) || []
  )
})

const selectedOptions = computed(() => {
  if (!modelValue.value) return []
  const ids = Array.isArray(modelValue.value) ? modelValue.value : [modelValue.value]
  return availableColumns.value.filter((option) => ids.includes(option.id as number))
})

const groupedOpportunities = computed(() => {
  if (!stages.value || !props.data) return []

  const grouped = stages.value.map((stage) => ({
    stageId: stage.id,
    stageName: stage.name,
    sequence: stage.sequence,
    opportunities: props.data?.filter(
      (opportunity) => Number(opportunity.stageId) === Number(stage.id),
    ),
  }))

  const sel = selectedOptions.value
  return grouped
    .sort((a, b) => a.sequence - b.sequence)
    .filter((group) => (sel.length === 0 ? true : sel.some((col) => col.id === group.stageId)))
})

const context = inject<Context>('context', '')

const changeStage = async (opportunityId: number, stageId: number) => {
  await opportunityStore
    .updateOpportunity(opportunityId, { stageId: stageId }, context)
    .then(() => {
      $toast.success("L'affaire a été déplacée.")
    })
    .catch(() => {
      $toast.error("Erreur lors de l'enregistrement.")
    })
}

const calculateTotalExpectedRevenue = (opportunities: Opportunity[]) => {
  return opportunities.reduce(
    (total, opportunity) => total + (opportunity.expectedRevenue + opportunity.recurringRevenue),
    0,
  )
}

function interpolateColors(startColor: string, endColor: string, steps: number) {
  const start = {
    r: parseInt(startColor.slice(1, 3), 16),
    g: parseInt(startColor.slice(3, 5), 16),
    b: parseInt(startColor.slice(5, 7), 16),
  }
  const end = {
    r: parseInt(endColor.slice(1, 3), 16),
    g: parseInt(endColor.slice(3, 5), 16),
    b: parseInt(endColor.slice(5, 7), 16),
  }

  const colors = []
  for (let i = 0; i < steps; i++) {
    const r = Math.round(start.r + (end.r - start.r) * (i / (steps - 1)))
    const g = Math.round(start.g + (end.g - start.g) * (i / (steps - 1)))
    const b = Math.round(start.b + (end.b - start.b) * (i / (steps - 1)))
    colors.push(
      `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`,
    )
  }
  return colors
}

function getLuminance(hexColor: string): number {
  const r = parseInt(hexColor.slice(1, 3), 16) / 255
  const g = parseInt(hexColor.slice(3, 5), 16) / 255
  const b = parseInt(hexColor.slice(5, 7), 16) / 255

  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function getBgColor(stageName: string) {
  const startColor = '#46dbc9' // Couleur claire
  const endColor = '#185a7d'
  const totalStages = stages.value!.length

  const colorPalette = interpolateColors(startColor, endColor, totalStages)

  const stageIndex = stages.value!.findIndex(
    (stage) => stage.name.toLowerCase() === stageName.toLowerCase(),
  )

  return stageIndex !== -1 ? colorPalette[stageIndex] : '#000000'
}

function getTextColor(stageName: string) {
  const backgroundColor = getBgColor(stageName)
  return getLuminance(backgroundColor) > 0.5 ? '#132144' : '#f0f0f0'
}

onMounted(async () => {
  await opportunityStore.getAllStages()
  const savedSelection = Cookies.get(COOKIE_NAME)
  if (savedSelection) {
    try {
      modelValue.value = JSON.parse(savedSelection)
    } catch (error) {
      modelValue.value = availableColumns.value.map((col) => col.id)
    }
  } else {
    // Si pas de cookie, sélectionner toutes les colonnes par défaut
    modelValue.value = availableColumns.value.map((col) => col.id)
  }
})

// Fonction pour gérer le changement de sélection
const handleModelUpdate = (value: any) => {
  modelValue.value = value ? value.map((item: any) => item.id) : []
  // Enregistrer la sélection dans un cookie qui n'est pas supprimé à la fermeture du navigateur et qui n'expire pas
  Cookies.set(COOKIE_NAME, JSON.stringify(modelValue.value), {
    expires: COOKIE_EXPIRY,
    secure: true,
  })
}
const resetSelection = () => {
  modelValue.value = availableColumns.value.map((col) => col.id)
  Cookies.remove(COOKIE_NAME)
}
</script>

<template>
  <div class="grid grid-cols-1 mb-2 max-w-full w-fit">
    <div class="gap-2">
      <span class="label label-text font-semibold">Personnalisation affichage</span>
      <label class="swap swap-flip m-2">
        <input type="checkbox" v-model="viewSelectColumns" />
        <div class="swap-on tooltip tooltip-right" data-tip="masquer">
          <component :is="EyeIcon" class="w-5 h-5" />
        </div>
        <div class="swap-off tooltip tooltip-right" data-tip="afficher">
          <component :is="EyeSlashIcon" class="w-5 h-5" />
        </div>
      </label>
    </div>
    <div :class="{ 'join gap-2': true, hidden: !viewSelectColumns }">
      <Multiselect
        :model-value="selectedOptions"
        @update:model-value="handleModelUpdate"
        :options="availableColumns"
        multiple
        :close-on-select="true"
        :searchable="true"
        placeholder="Sélectionner colonnes"
        select-label="Sélectionner"
        selected-label="Sélectionné"
        deselect-label="Retirer"
        label="text"
        track-by="id"
        class="join-item my-1"
      />
      <div class="tooltip tooltip-right join-item" data-tip="Reinitialiser la sélection">
        <button-daisy text="" :icon="ArrowPathIcon" @click="resetSelection" />
      </div>
    </div>
  </div>

  <div class="flex gap-4 justify-items-center">
    <div
      v-for="opportunities in groupedOpportunities"
      :key="opportunities.stageId"
      :data-stage-id="opportunities.stageId"
      class="min-w-80 w-80 bg-base-100 rounded-3xl bg-opacity-40 mb-4"
    >
      <div
        v-if="opportunities.opportunities"
        class="text-md m-2 bg-primary text-secondary rounded-2xl"
        :style="{
          'background-color': getBgColor(opportunities.stageName),
          color: getTextColor(opportunities.stageName),
        }"
      >
        <p class="pl-4 pt-1 flex items-center justify-between">
          <span class="flex items-center">
            <span class="text-2xl font-bold mx-3">{{ opportunities.opportunities?.length }}</span>
            {{ opportunities.stageName }}
          </span>
        </p>
        <p class="pb-1 mx-3 pe-2 text-right text-xl font-bold">
          {{ printCurrency(calculateTotalExpectedRevenue(opportunities.opportunities)) }}
        </p>
      </div>
      <draggable-list
        v-if="opportunities.opportunities"
        :listOpportunity="opportunities.opportunities"
        :stage-id="opportunities.stageId"
        @end="(event) => changeStage(event.opportunityId, event.newStageId)"
      />
    </div>
  </div>
</template>
